import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import toast from "react-hot-toast";

export default function LinkPayment({ isPaymentLinked, className, style })  {
  const userId = localStorage.getItem("logged_user_id");
  const [loading, setLoading] = useState(false);
  const [showBusinessAccountInfo, setShowBusinessAccountInfo] = useState(false);
  const [showPaymentsReceivableError, setShowPaymentsReceivableError] = useState(false);
  const [showEmailConfirmationError, setShowEmailConfirmationError] = useState(false);
  const [showApiMessageDialog, setShowApiMessageDialog] = useState(false);
  const [apiMessage, setApiMessage] = useState("");

  const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const CLIENT_SECRET = process.env.REACT_APP_PAYPAL_SECRET_KEY;

  const handleOnboarding = async () => {
    setLoading(true);
    try {
      const basicAuth = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);

      const tokenRes = await fetch("https://api-m.paypal.com/v1/oauth2/token", {
        method: "POST",
        headers: {
          Authorization: `Basic ${basicAuth}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: "grant_type=client_credentials",
      });

      const tokenData = await tokenRes.json();
      const accessToken = tokenData.access_token;
     
      const currentPath = window.location.pathname.startsWith("/")
        ? window.location.pathname.slice(1)
        : window.location.pathname;

      const referralRes = await fetch(
        "https://chefrepublicwebapi-dev.portalwiz.in/api/user/partner_referral",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            operations: [
              {
                operation: "API_INTEGRATION",
                api_integration_preference: {
                  rest_api_integration: {
                    integration_method: "PAYPAL",
                    integration_type: "THIRD_PARTY",
                    third_party_details: {
                      features: [
                        "PAYMENT",
                        "REFUND",
                        "PARTNER_FEE",
                        "VAULT",
                        "BILLING_AGREEMENT",
                        "ACCESS_MERCHANT_INFORMATION",
                      ],
                    },
                  },
                },
              },
            ],
            products: ["PPCP", "ADVANCED_VAULTING"],
            capabilities: ["PAYPAL_WALLET_VAULTING_ADVANCED"],
            partner_config_override: {
              return_url: `https://chefrepublic-dev.portalwiz.in/${currentPath}`,
              return_url_description: "Back to Chef Republic",
              show_add_credit_card: true,
              return_url_status: "SUCCESS",
            },
            legal_consents: [
              {
                type: "SHARE_DATA_CONSENT",
                granted: true,
              },
            ],
          }),
        }
      );

      const referralData = await referralRes.json();

      // Prefer signup_link if available, otherwise fallback to action_url
      const signupLink = referralData?.signup_link;
      const actionUrl = referralData?.paypal_response?.links?.find(
        (link) => link.rel === "action_url"
      )?.href;

      const redirectUrl = signupLink || actionUrl;

      if (redirectUrl) {
        console.log("🔗 Redirecting user to PayPal:", redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.error("⚠️ No redirect URL found in PayPal response", referralData);
        setShowBusinessAccountInfo(true);
      }
    } catch (err) {
      setShowBusinessAccountInfo(true);
      console.error("❌ Onboarding error:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const merchantId = urlParams.get("merchantIdInPayPal");
    const email = urlParams.get("email");
    if (merchantId) {
      saveMerchantIdToBackend(merchantId, email);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  const saveMerchantIdToBackend = async (merchantId, email) => {
    try {
      const response = await fetch(
        "https://chefrepublicwebapi-dev.portalwiz.in/api/user/add_chef_account",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            merchantId,
            Email: email,
            merchantIdInPayPal: merchantId,
            permissionsGranted: "",
            accountStatus: " ",
            consentStatus: " ",
            isEmailConfirmed: " ",
            returnMessage: " ",
          }),
        }
      );

      const data = await response.json();
      console.log("🔍 API Response:", data);

      if (data.message) {
        setApiMessage(data.message);
        setShowApiMessageDialog(true);
      }

      if (response.ok && data.status === true) {
        checkAccountStatus(merchantId);
      } else {
        console.error("❌ API Error:", data.message || "Unknown error");
      }
    } catch (err) {
      console.error("Save merchant error:", err);
      setApiMessage("An error occurred while saving merchant information.");
      setShowApiMessageDialog(true);
    }
  };

  const checkAccountStatus = async (merchantId) => {
    try {
      const basicAuth = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
      const tokenRes = await fetch("https://api-m.paypal.com/v1/oauth2/token", {
        method: "POST",
        headers: {
          Authorization: `Basic ${basicAuth}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: "grant_type=client_credentials",
      });
      const tokenData = await tokenRes.json();
      const accessToken = tokenData.access_token;
      const PARTNER_ID = "5ZPAUD3E2QNVY";

      const statusRes = await fetch(
        `https://api-m.paypal.com/v1/customer/partners/${PARTNER_ID}/merchant-integrations/${merchantId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const statusData = await statusRes.json();
      console.log("📡 Account Status:", statusData);

      if (statusData.payments_receivable === false) {
        setShowPaymentsReceivableError(true);
      } else if (statusData.primary_email_confirmed === false) {
        setShowEmailConfirmationError(true);
      } else {
        toast.success("✅ Merchant account saved successfully");
        // setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      console.error("Account status check error:", err);
    }
  };

  return (
    <Box>
      {/* Payments Receivable Error */}
      <Dialog
        open={showPaymentsReceivableError}
        onClose={() => setShowPaymentsReceivableError(false)}
      >
        <Box sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom color="error">
            Attention
          </Typography>
          <Typography variant="body1" paragraph>
            You currently cannot receive payments due to restriction on your PayPal account.
            Please reach out to PayPal Customer Support or visit{" "}
            <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer">
              paypal.com
            </a>{" "}
            for more information.
          </Typography>
          <Button onClick={() => setShowPaymentsReceivableError(false)}>Close</Button>
        </Box>
      </Dialog>

      {/* Primary Email Not Confirmed Error */}
      <Dialog
        open={showEmailConfirmationError}
        onClose={() => setShowEmailConfirmationError(false)}
      >
        <Box sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom color="error">
            Attention
          </Typography>
          <Typography variant="body1" paragraph>
            Please confirm your email address on{" "}
            <a
              href="https://www.paypal.com/businessprofile/settings"
              target="_blank"
              rel="noopener noreferrer"
            >
              PayPal Business Settings
            </a>{" "}
            in order to receive payments! You currently cannot receive payments.
          </Typography>
          <Button onClick={() => setShowEmailConfirmationError(false)}>Close</Button>
        </Box>
      </Dialog>

      {/* API Message Dialog */}
      <Dialog
        open={showApiMessageDialog}
        onClose={() => setShowApiMessageDialog(false)}
      >
        <Box sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Message
          </Typography>
          <div
            style={{ marginBottom: '16px', fontSize: '1rem', lineHeight: '1.5' }}
            dangerouslySetInnerHTML={{ __html: apiMessage }}
          />
          <Button 
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#FF5E41",
              borderRadius: "50px",
              "&:hover": {
                backgroundColor: "#fff",
                color: "#FF5E41",
                border: "1px solid #ffb199",
              },
            }}
            onClick={() => setShowApiMessageDialog(false)}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Main Button - now directly triggers PayPal onboarding */}
      <button
        type="button"
        className={className || "btn btn-orange rounded-pill mb-3"}
        disabled={isPaymentLinked}
        onClick={handleOnboarding}
        style={{
          width: "100%",
          letterSpacing: "1px",
          backgroundColor: isPaymentLinked ? "#cccbcbff" : "#FF5E41",
          color: "#fff",
          opacity: 1,
          border: "none",
          cursor: isPaymentLinked ? "not-allowed" : "pointer",
          transition: "all 0.3s ease",
          ...style,
          "&:hover": {
            backgroundColor: isPaymentLinked ? "#cccbcbff" : "#fff",
            color: isPaymentLinked ? "#fff" : "#FF5E41",
            border: isPaymentLinked ? "none" : "1px solid #ffb199",
          },
        }}
      >
        <span>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Link Payment Method&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </button>

      {/* Business Account Info Dialog */}
      <Dialog
        open={showBusinessAccountInfo}
        onClose={() => setShowBusinessAccountInfo(false)}
      >
        <Box sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            PayPal Business Account Required
          </Typography>
          <Typography variant="body1" paragraph>
            To receive payments and sell services, you need a PayPal Business Account.
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
            Benefits of a Business Account:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                Accept PayPal, Venmo (US), and Pay Later payments
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Process credit & debit cards directly</Typography>
            </li>
            <li>
              <Typography variant="body2">Access to Apple Pay & other payment methods</Typography>
            </li>
          </ul>
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#FF5E41",
              color: "#fff",
              borderRadius: "50px",
              mt: 2,
              "&:hover": {
                backgroundColor: "#fff",
                color: "#FF5E41",
              },
            }}
            onClick={() => {
              setShowBusinessAccountInfo(false);
              handleOnboarding(); // Directly start onboarding without OTP
            }}
          >
            Try Again with Business Account
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}